import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { apiFetch } from '@/lib/apiClient';
import { selectIsLoggedIn } from '@/store/slices/authSlice';
import {
  socialKeys,
  PaginationParams,
  FeedParams,
  CheckInsParams,
  UserSearchParams,
  NotificationsParams,
} from '@/api/keys';

// Re-export types for backward compatibility
export type {
  MinimalUser,
  UserBasic,
  UserBasicPaginatedResponse,
  PaginatedResponse,
  UserSearchResult,
  UserSearchResponse,
  UserProfileStats,
  UserProfileResponse,
  BlockedUser,
  PostImage,
  PostRestaurant,
  PostList,
  Post,
  PostsResponse,
  CheckIn,
  CheckInsResponse,
  Relationship,
  Comment,
  Notification,
  NotificationsResponse,
  NotificationPreferences,
  FeedActivity,
  GroupedActivity,
  GroupedItem,
  Announcement,
  FeedItem,
  FeedResponse,
  Report,
  ActivityType,
  FeedItemType,
  // Story types
  Story,
  StoryView,
  UserStoriesGroup,
  StoriesResponse,
  StoryMediaType,
} from '@/types/social';

// Import types for function signatures
import type {
  PostsResponse,
  Post,
  FeedResponse,
  FeedActivity,
  CheckInsResponse,
  UserBasic,
  UserBasicPaginatedResponse,
  PaginatedResponse,
  Relationship,
  UserSearchResult,
  UserSearchResponse,
  UserProfileResponse,
  Comment,
  NotificationsResponse,
  NotificationPreferences,
  BlockedUser,
  Report,
  Story,
  StoryView,
  UserStoriesGroup,
  StoriesResponse,
} from '@/types/social';

// ============ Posts ============

/**
 * Hook to get a user's posts
 */
export function useGetUserPosts(
  userId: number,
  params?: PaginationParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: socialKeys.userPosts(userId, params),
    queryFn: () =>
      apiFetch<PostsResponse>(`/social/posts/user/${userId}/`, {
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 20,
        },
      }),
    enabled: options?.enabled ?? !!userId,
  });
}

/**
 * Hook to get a single post by URL key
 */
export function useGetPostByUrlKey(urlKey: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.post(urlKey),
    queryFn: () => apiFetch<Post>(`/social/posts/${urlKey}/`),
    enabled: options?.enabled ?? !!urlKey,
  });
}

/**
 * Hook to get a single feed activity by URL key
 */
export function useGetFeedActivityByUrlKey(urlKey: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.feedActivity(urlKey),
    queryFn: () => apiFetch<FeedActivity>(`/social/feed/${urlKey}/`),
    enabled: options?.enabled ?? !!urlKey,
  });
}

// ============ Feed ============

/**
 * Hook to get personalized feed
 */
export function useGetFeed(params?: FeedParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.feedList(params),
    queryFn: () =>
      apiFetch<FeedResponse>('/social/feed/', {
        params: {
          page: params?.page ?? 1,
          page_size: params?.pageSize ?? 50,
          content_type: params?.contentType ?? 'all',
        },
      }),
    enabled: options?.enabled,
  });
}

// ============ Check-ins ============

/**
 * Hook to get a user's check-ins
 */
export function useGetUserCheckIns(
  userId: number,
  params?: PaginationParams,
  options?: { enabled?: boolean }
) {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  return useQuery({
    queryKey: socialKeys.userCheckIns(userId, params),
    queryFn: () =>
      apiFetch<CheckInsResponse>(`/social/checkins/user/${userId}/`, {
        params: {
          page: params?.page ?? 1,
          pageSize: params?.pageSize ?? 20,
        },
      }),
    // Only fetch for authenticated users
    enabled: (options?.enabled ?? !!userId) && isLoggedIn,
  });
}

/**
 * Hook to get check-ins with filters
 */
export function useGetCheckIns(params?: CheckInsParams, options?: { enabled?: boolean }) {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  return useQuery({
    queryKey: socialKeys.checkInsList(params),
    queryFn: () =>
      apiFetch<CheckInsResponse>('/social/checkins/', {
        params: {
          ...(params?.restaurantId && { restaurant: params.restaurantId }),
          ...(params?.fromConnections && { from_connections: true }),
          ...(params?.days && { days: params.days }),
          page: params?.page ?? 1,
          page_size: params?.pageSize ?? 20,
        },
      }),
    // Only fetch for authenticated users
    enabled: (options?.enabled ?? true) && isLoggedIn,
  });
}

// ============ Relationships ============

/**
 * Hook to get friends list
 */
export function useGetFriends(options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.friends(),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>('/social/relationships/friends/', {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled,
  });
}

/**
 * Hook to get followers
 */
export function useGetFollowers(options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.followers(),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>('/social/relationships/followers/', {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled,
  });
}

/**
 * Hook to get following list
 */
export function useGetFollowing(options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.following(),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>('/social/relationships/following/', {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled,
  });
}

/**
 * Hook to get a specific user's friends
 */
export function useGetUserFriends(userId: number, options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.userFriends(userId),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>(`/social/relationships/friends/${userId}/`, {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled ?? !!userId,
  });
}

/**
 * Hook to get a specific user's followers
 */
export function useGetUserFollowers(userId: number, options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.userFollowers(userId),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>(`/social/relationships/followers/${userId}/`, {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled ?? !!userId,
  });
}

/**
 * Hook to get a specific user's following
 */
export function useGetUserFollowing(userId: number, options?: { enabled?: boolean; pageSize?: number }) {
  const pageSize = options?.pageSize ?? 20;
  return useInfiniteQuery({
    queryKey: socialKeys.userFollowing(userId),
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<UserBasicPaginatedResponse>(`/social/relationships/following/${userId}/`, {
        params: { page: pageParam, page_size: pageSize },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page') || '1', 10);
    },
    enabled: options?.enabled ?? !!userId,
  });
}

// ============ User Search & Profiles ============

/**
 * Hook to search users
 */
export function useSearchUsers(params: UserSearchParams, options?: { enabled?: boolean }) {
  const hasValidSearch =
    (params.q?.length ?? 0) >= 2 ||
    (params.name?.length ?? 0) >= 2 ||
    (params.location?.length ?? 0) >= 2;

  return useQuery({
    queryKey: socialKeys.userSearch(params),
    queryFn: () =>
      apiFetch<UserSearchResponse>('/social/users/search/', {
        params: {
          ...(params.q && { q: params.q }),
          ...(params.name && { name: params.name }),
          ...(params.location && { location: params.location }),
          limit: params.limit ?? 20,
          ...(params.friendsOnly && { friends_only: 'true' }),
          ...(params.memberIds?.length && { member_ids: params.memberIds.join(',') }),
        },
      }),
    enabled: (options?.enabled ?? true) && hasValidSearch,
  });
}

/**
 * Hook to get user profile by ID
 */
export function useGetUserProfile(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.userProfile(userId),
    queryFn: () => apiFetch<UserProfileResponse>(`/social/users/${userId}/`),
    enabled: options?.enabled ?? !!userId,
  });
}

/**
 * Hook to get suggested users for onboarding
 * Returns users based on mutual friends and location similarity
 * Also returns contactMatches - users who were in your synced contacts and have since joined
 */
export function useSuggestedUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.userSuggestions(),
    queryFn: () =>
      apiFetch<{ suggestions: UserSearchResult[]; contactMatches: UserSearchResult[] }>('/social/users/suggestions/'),
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// ============ Comments ============

/**
 * Hook to get post comments
 */
export function useGetPostComments(postId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.postComments(postId),
    queryFn: () => apiFetch<Comment[]>(`/social/posts/${postId}/comments/`),
    enabled: options?.enabled ?? !!postId,
  });
}

/**
 * Hook to get activity comments
 */
export function useGetActivityComments(
  activityId: number | string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: socialKeys.activityComments(activityId),
    queryFn: () => apiFetch<Comment[]>(`/social/feed/${activityId}/comments/`),
    enabled: options?.enabled ?? !!activityId,
  });
}

// ============ Notifications ============

/**
 * Hook to get notifications
 */
export function useGetNotifications(
  params?: NotificationsParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: socialKeys.notificationsList(params),
    queryFn: () =>
      apiFetch<NotificationsResponse>('/social/notifications/', {
        params: {
          page: params?.page ?? 1,
          page_size: params?.pageSize ?? 20,
          ...(params?.isRead !== undefined && { is_read: params.isRead }),
        },
      }),
    enabled: options?.enabled,
  });
}

/**
 * Hook to get unread notification count
 */
export function useGetUnreadCount(options?: { enabled?: boolean; refetchInterval?: number }) {
  const isLoggedIn = useSelector(selectIsLoggedIn);

  return useQuery({
    queryKey: socialKeys.unreadCount(),
    queryFn: () => apiFetch<{ unreadCount: number }>('/social/notifications/unread-count/'),
    // Only poll for authenticated users
    enabled: (options?.enabled ?? true) && isLoggedIn,
    refetchInterval: isLoggedIn ? options?.refetchInterval : false,
  });
}

/**
 * Hook to get notification preferences
 */
export function useGetNotificationPreferences(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.preferences(),
    queryFn: () => apiFetch<NotificationPreferences>('/social/notifications/preferences/'),
    enabled: options?.enabled,
  });
}

// ============ Blocked Users & Reports ============

/**
 * Hook to get blocked users
 */
export function useGetBlockedUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.blocked(),
    queryFn: () => apiFetch<BlockedUser[]>('/social/users/blocked/'),
    enabled: options?.enabled,
  });
}

/**
 * Hook to get user's reports
 */
export function useGetReports(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.reports(),
    queryFn: () => apiFetch<PaginatedResponse<Report>>('/social/reports/'),
    enabled: options?.enabled,
  });
}

// ============ Stories ============

/**
 * Hook to get stories from connections (feed)
 */
export function useGetStoriesFeed(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: socialKeys.storiesFeed(),
    queryFn: () => apiFetch<StoriesResponse>('/social/stories/'),
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to get current user's active stories
 */
export function useGetMyStories(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.myStories(),
    queryFn: () => apiFetch<{ results: Story[] }>('/social/stories/my/'),
    enabled: options?.enabled,
  });
}

/**
 * Hook to get a specific user's stories
 */
export function useGetUserStories(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.userStories(userId),
    queryFn: () =>
      apiFetch<{ user: UserBasic; stories: Story[]; hasUnviewed: boolean }>(
        `/social/stories/user/${userId}/`
      ),
    enabled: options?.enabled ?? !!userId,
  });
}

/**
 * Hook to get a specific story
 */
export function useGetStory(storyId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.story(storyId),
    queryFn: () => apiFetch<Story>(`/social/stories/${storyId}/`),
    enabled: options?.enabled ?? !!storyId,
  });
}

/**
 * Hook to get story views (for story owner)
 */
export function useGetStoryViews(storyId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: socialKeys.storyViews(storyId),
    queryFn: () => apiFetch<{ results: StoryView[] }>(`/social/stories/${storyId}/views/`),
    enabled: options?.enabled ?? !!storyId,
  });
}
